import { SelectHTMLAttributes, forwardRef } from 'react';
import { classNames } from '@/utils/classNames';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  size?: 'sm' | 'md' | 'lg';
  placeholder?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      options,
      size = 'md',
      placeholder = '選択してください',
      className,
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-sm',
      lg: 'px-5 py-4 text-base',
    };

    const selectClasses = classNames(
      'block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm transition-colors',
      'focus:border-primary-500 focus:ring-primary-500',
      'bg-white dark:bg-gray-700 text-gray-900 dark:text-white',
      'min-h-[44px] touch-target',
      sizeClasses[size],
      error &&
        'border-red-300 dark:border-red-600 text-red-900 dark:text-red-400 focus:border-red-500 focus:ring-red-500',
      props.disabled &&
        'bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed',
      className
    );

    return (
      <div className='space-y-1'>
        {label && (
          <label
            className={classNames(
              'block text-sm font-medium',
              error ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
            )}
          >
            {label}
          </label>
        )}
        <select ref={ref} className={selectClasses} {...props}>
          {placeholder && (
            <option value='' disabled>
              {placeholder}
            </option>
          )}
          {options.map(option => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        {error && <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>}
        {helperText && !error && (
          <p className='text-sm text-gray-500 dark:text-gray-400'>{helperText}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
